<script setup lang="ts">
import { computed } from "vue";

import type { BarChartData } from "@/model/chart";

const props = defineProps<{ value: BarChartData<unknown, unknown> }>();

const isEmpty = computed(() => props.value.total === 0);
</script>

<template>
  <div class="stacked-bar-chart">
    <template v-if="!isEmpty">
      <div
        v-for="(item, index) in value.items"
        :key="index"
        class="progress-bar bordered"
        :style="{ backgroundColor: item.color, '--bar-width': item.width + '%' }"
      />
    </template>
    <svg
      v-else
      class="empty-bar bordered"
      viewBox="0 0 500 40"
      preserveAspectRatio="none"
      role="presentation"
    >
      <defs>
        <pattern
          id="diagonalHatch"
          patternUnits="userSpaceOnUse"
          width="4"
          height="8"
          patternTransform="rotate(-45 2 2)"
        >
          <path d="M -1,2 l 6,0" stroke-width=".5" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#diagonalHatch)" />
    </svg>
  </div>
</template>

<style lang="scss" scoped>
@use "@/styles/board";
@use "@/styles/variables/colors";

// Register so border width is always a real length: 0 becomes 0px
@property --distribution-border {
  syntax: "<length>";
  inherits: false;
  initial-value: 0px; // stylelint-disable-line length-zero-no-unit -- needs an explicit unit for use in calc()
}

.stacked-bar-chart {
  position: relative;
  width: 100%;
  height: 100%;

  .bordered {
    // Border width supplied by the consumer via --distribution-border-width
    // (a <length>, e.g. 2px or 0px), defaulting to the board-scaled 2px. The
    // @property above coerces it; a unitless non-zero value yields no border.
    --distribution-border: var(--distribution-border-width, #{board.len(2px)});

    position: absolute;
    height: 100%;
    border-radius: board.len(20px);
    border: var(--distribution-border) solid colors.$background-ui;
  }

  .progress-bar {
    // Add the border (both sides) back into the width so a segment narrower
    // than 2× the border still shows its colour instead of collapsing into its
    // border. Capped at the track width so the full-width bar never overflows.
    width: min(calc(var(--bar-width) + var(--distribution-border) * 2), 100%);
  }

  .empty-bar {
    width: 100%;

    path {
      stroke: colors.$text-subtle;
    }
  }
}
</style>
